import { createConnectIntegration } from "@coten-inc/coten-auth-spa";

import type { AuthenticatedFetch } from "~/lib/apolloClient";

export const connectCallbackPath = "/connect-login/callback";
const devConnectEnabled =
  import.meta.env.VITE_COTEN_AUTH_ENVIRONMENT === "dev" &&
  window.location.origin === "https://dewey.coten.dev" &&
  !import.meta.env.VITE_COTEN_CONNECT_ORIGINS?.trim();
const environment = import.meta.env.VITE_COTEN_AUTH_ENVIRONMENT;
const configuredDeployment =
  (environment === "dev" || environment === "prd") &&
  Boolean(
    import.meta.env.VITE_COTEN_CONNECT_ORIGINS?.split(",")
      .map((origin: string) => origin.trim())
      .includes(window.location.origin),
  );
if (
  configuredDeployment &&
  (!import.meta.env.VITE_COTEN_CONNECT_CLIENT_ID?.trim() ||
    !import.meta.env.VITE_COTEN_CONNECT_ISSUER?.trim() ||
    !import.meta.env.VITE_COTEN_CONNECT_RESOURCE?.trim())
) {
  throw new Error("Connect configuration is incomplete");
}
const localClientId =
  import.meta.env.DEV && import.meta.env.VITE_COTEN_AUTH_ENVIRONMENT === "local"
    ? import.meta.env.VITE_COTEN_CONNECT_CLIENT_ID
    : undefined;
const clientId = devConnectEnabled
  ? "client_01M47SWCX4A2Y5D6BEZMS3A1J0"
  : configuredDeployment
    ? import.meta.env.VITE_COTEN_CONNECT_CLIENT_ID
    : localClientId;
const enabled = Boolean(clientId);
const returnToKey = "dewey:connect-return-to";

export const isConnectCallback = () => enabled && window.location.pathname === connectCallbackPath;

const connect = enabled
  ? createConnectIntegration({
      environment: devConnectEnabled ? "dev" : configuredDeployment ? environment : "local",
      issuer: devConnectEnabled
        ? "https://upbeat-show-45-staging.authkit.app"
        : import.meta.env.VITE_COTEN_CONNECT_ISSUER,
      clientId,
      resource: devConnectEnabled ? "https://cnut.coten.dev" : import.meta.env.VITE_COTEN_CONNECT_RESOURCE,
      scopes: ["cnut:access"],
      redirectUri: `${window.location.origin}${connectCallbackPath}`,
      apiOrigins: [new URL(import.meta.env.VITE_HASURA_URL).origin, new URL(import.meta.env.VITE_CNUT_URL).origin],
    })
  : undefined;

export const connectIntegration = connect?.integration;

export const completeConnectCallback = async (): Promise<void> => {
  try {
    await connect?.initialize();
  } finally {
    const destination = window.sessionStorage.getItem(returnToKey);
    window.sessionStorage.removeItem(returnToKey);
    let safeDestination = "/sign-in";
    try {
      const url = new URL(destination ?? "/sign-in", window.location.origin);
      if (url.origin === window.location.origin && !["/callback", connectCallbackPath].includes(url.pathname)) {
        safeDestination = `${url.pathname}${url.search}${url.hash}`;
      }
    } catch {
      // 保存値が破損していても認証codeを残さずログイン画面へ戻す。
    }
    window.history.replaceState(window.history.state, "", safeDestination);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }
};

export const authorizeConnect = async (retry = false): Promise<boolean> => {
  if (!connect) return true;
  const destination = new URL(window.location.href);
  for (const field of ["code", "state", "error", "error_description"]) destination.searchParams.delete(field);
  window.sessionStorage.setItem(returnToKey, `${destination.pathname}${destination.search}${destination.hash}`);
  const ready = await connect.authorize({ retry });
  if (ready) window.sessionStorage.removeItem(returnToKey);
  return ready;
};

export const clearConnect = (): void => {
  connect?.clear();
  window.sessionStorage.removeItem(returnToKey);
};

export const getConnectAuthenticatedFetch = (legacy: AuthenticatedFetch): AuthenticatedFetch =>
  connect?.fetch ?? legacy;
